import type { ContentFormat } from '@/lib/contentFormat';

/**
 * The format an editor treats its content as — drives which editor
 * component (raw-text / live-edit tokens) is appropriate, and which one
 * the `ContentFormat` dispatcher should render it with.
 *
 * Kept narrower than the render-side `ContentFormat` union on purpose:
 * there is no Markdown-specific or plaintext-specific editor, only
 * Djot and AsciiDoc ones, so the only question an editor ever has to
 * answer is "which editor component should handle this?".
 */
export type EditorFormat = 'djot' | 'asciidoc';

/**
 * Which editor component is the right fit for a given `ContentFormat`:
 *
 *  - `djot` and `markdown` → the Djot editor. Djot and Markdown share
 *    `#` ATX headings, `[text](url)` links, and most inline markup, so
 *    the Djot editor's toolbar/live tokens are the best approximation
 *    available.
 *  - `asciidoc` → the AsciiDoc editor — the only format with a
 *    dedicated editor at all.
 *  - `plaintext` → the Djot editor as well, since it's also the rawest
 *    raw-text option when there's no markup to style.
 */
export function editorFormatFor(format: 'djot' | 'asciidoc' | 'markdown' | 'plaintext'): EditorFormat {
  return format === 'asciidoc' ? 'asciidoc' : 'djot';
}

export const EDITOR_FORMAT_LABELS: Record<EditorFormat, string> = {
  djot: 'Djot',
  asciidoc: 'AsciiDoc',
};

/** Which editing surface the placeholder is for — wording differs between articles and publication sections, length between normal and focus mode. */
export type EditorPlaceholderScope = 'article' | 'article-focus' | 'section' | 'section-focus';

const EDITOR_PLACEHOLDERS: Record<ContentFormat, Record<EditorPlaceholderScope, string>> = {
  djot: {
    article:
      'Write your article in Djot markup.\n\nUse [[Article Name]] to link to other wiki articles.\n\n# Heading\n\nSome _emphasized_ and *strong* text.',
    'article-focus': '# Heading\n\nSome _emphasized_ and *strong* text.',
    section:
      'Write this section in Djot markup.\n\n# A heading\n\nSome *strong* and _emphasized_ text.\n\nUse [[Article Name]] to link to a wiki article.',
    'section-focus': 'Write this section in Djot markup.',
  },
  asciidoc: {
    article:
      'Write your article in AsciiDoc markup.\n\nUse [[Article Name]] to link to other wiki articles.\n\n= A heading\n\nSome *strong* text.',
    'article-focus': '= A heading\n\nSome *strong* text.',
    section:
      'Write this section in AsciiDoc markup.\n\n== A heading\n\nSome *strong* and _emphasized_ text.\n\nUse [[Article Name]] to link to a wiki article.',
    'section-focus': 'Write this section in AsciiDoc markup.',
  },
  markdown: {
    article:
      'Write your article in Markdown.\n\nUse [[Article Name]] to link to other wiki articles.\n\n# Heading\n\nSome _emphasized_ and **strong** text.',
    'article-focus': '# Heading\n\nSome _emphasized_ and **strong** text.',
    section:
      'Write this section in Markdown.\n\n# A heading\n\nSome **strong** and _emphasized_ text.\n\nUse [[Article Name]] to link to a wiki article.',
    'section-focus': 'Write this section in Markdown.',
  },
  plaintext: {
    article: 'Write your article as plain text.\n\nNo markup — paragraphs and line breaks are kept exactly as written.',
    'article-focus': 'Plain text — no markup, paragraphs kept as written.',
    section: 'Write this section as plain text.\n\nNo markup — paragraphs and line breaks are kept exactly as written.',
    'section-focus': 'Write this section as plain text.',
  },
};

/**
 * The placeholder ("description") text for the content editor, per the
 * selected `ContentFormat`. Markdown and Plain text share the Djot
 * editor component (see `editorFormatFor`), but the placeholder must
 * still name and demonstrate the *selected* format — telling a writer
 * who picked Markdown to "write in Djot markup" is how format confusion
 * starts.
 */
export function editorPlaceholder(format: ContentFormat, scope: EditorPlaceholderScope): string {
  return EDITOR_PLACEHOLDERS[format][scope];
}
